import { useReveal, reveal } from '../hooks/useReveal';

// YC Logo as inline SVG
const YCLogo = () => (
  <svg viewBox="0 0 100 100" className="w-12 h-12" fill="currentColor">
    <rect width="100" height="100" rx="8" fill="#FF6600"/>
    <path d="M30 25L50 55L70 25H62L50 45L38 25H30Z" fill="white"/>
    <rect x="46" y="50" width="8" height="25" fill="white"/>
  </svg>
);

export default function Building() {
  const [precurionRef, precurionRevealed] = useReveal({ threshold: 0.1 });
  const [backgroundRef, backgroundRevealed] = useReveal({ threshold: 0.15 });

  return (
    <section className="py-32 md:py-40">
      {/* Section divider */}
      <div className="w-full h-px bg-border mb-24"></div>

      {/* PRECURION - Full bleed background treatment */}
      <div
        id="building"
        ref={precurionRef}
        className="relative -mx-6 sm:-mx-10 lg:-mx-16 px-6 sm:px-10 lg:px-16 py-20 mb-32"
      >
        {/* Background */}
        <div className="absolute inset-0 bg-foreground"></div>

        <div className="relative max-w-screen-xl mx-auto">
          <div className={`flex items-center gap-4 mb-8 ${reveal(precurionRevealed)}`}>
            <div className="w-16 h-1.5 bg-accent"></div>
            <p className="text-caption uppercase tracking-widest" style={{ color: 'rgba(250, 250, 248, 0.6)' }}>What I'm Building</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left - Content */}
            <div className="lg:col-span-7">
              <h2 className={`text-display-lg text-background mb-4 ${reveal(precurionRevealed)}`} style={{ '--d': '60ms' }}>
                Precurion
              </h2>
              <p className={`text-statement text-accent mb-10 ${reveal(precurionRevealed)}`} style={{ '--d': '120ms' }}>
                Trust layer for AI execution in finance.
              </p>

              <div className={`space-y-6 text-body ${reveal(precurionRevealed)}`} style={{ color: 'rgba(250, 250, 248, 0.7)', '--d': '180ms' }}>
                <p>
                  Financial analysts can model at AI speed, but build with conviction.
                  Every input is sourced to the document. Every assumption is traceable.
                  You don't blindly trust—you verify.
                </p>
                <p>
                  When a model says the asking price is $5M, where did that number come from?
                  What page? What contract? Is that contract even real?
                </p>
                <p className="text-background">
                  We're building verification layers that go deeper than "here's a source."
                  Is it contextually correct? Surface errors early, make assumptions traceable,
                  so you never have to review everything from scratch.
                </p>
              </div>
            </div>

            {/* Right - The Bet + Visual */}
            <div className={`lg:col-span-5 ${reveal(precurionRevealed)}`} style={{ '--d': '200ms' }}>
              {/* Visual representation */}
              <div className="relative mb-8 p-8 border" style={{ borderColor: 'rgba(250, 250, 248, 0.2)' }}>
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-accent"></div>
                    <span className="text-caption uppercase tracking-widest" style={{ color: 'rgba(250, 250, 248, 0.6)' }}>The Bet</span>
                  </div>
                </div>
                <p className="text-body mb-4" style={{ color: 'rgba(250, 250, 248, 0.7)' }}>
                  Excel was built 40 years ago for humans. Its positional encoding—A1, B2—is
                  optimized for human eyes, not AI.
                </p>
                <p className="text-body text-background font-medium">
                  Language models don't see grids. They see tokens.
                </p>
              </div>

              {/* Elegant flow indicator */}
              <div className="flex items-center gap-3 pt-4">
                <span className="text-label uppercase tracking-widest" style={{ color: 'rgba(250, 250, 248, 0.6)' }}>Source</span>
                <div className="flex-1 h-px max-w-16" style={{ backgroundColor: 'rgba(196, 164, 132, 0.4)' }}></div>
                <span className="text-label text-accent uppercase tracking-widest">Verify</span>
                <div className="flex-1 h-px bg-accent max-w-16"></div>
                <span className="text-label text-background uppercase tracking-widest font-medium">Trust</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CREDIBILITY - Visual proof */}
      <div ref={backgroundRef}>
        <div className={`flex items-center gap-4 mb-12 ${reveal(backgroundRevealed)}`}>
          <div className="w-16 h-1.5 bg-accent"></div>
          <p className="text-caption uppercase text-subtle tracking-widest">Background</p>
        </div>

        {/* Credibility bar - horizontal, visual */}
        <div className={`flex flex-wrap items-center gap-8 mb-16 pb-16 border-b border-border ${reveal(backgroundRevealed)}`} style={{ '--d': '60ms' }}>
          {/* YC */}
          <div className="flex items-center gap-4">
            <YCLogo />
            <div>
              <p className="text-body text-foreground font-medium">Y Combinator</p>
              <p className="text-caption text-muted">S23 Batch</p>
            </div>
          </div>

          <div className="hidden sm:block w-px h-12 bg-border"></div>

          {/* Cursor */}
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-foreground rounded flex items-center justify-center">
              <span className="text-background font-mono text-lg font-bold">{'>'}_</span>
            </div>
            <div>
              <p className="text-body text-foreground font-medium">Cursor</p>
              <p className="text-caption text-muted">Worked w/ current COO</p>
            </div>
          </div>

          <div className="hidden sm:block w-px h-12 bg-border"></div>

          {/* Technical founder */}
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 border-2 border-accent rounded flex items-center justify-center">
              <span className="text-accent font-mono text-sm">&lt;/&gt;</span>
            </div>
            <div>
              <p className="text-body text-foreground font-medium">Tinkerer</p>
              <p className="text-caption text-muted">Loves the work</p>
            </div>
          </div>
        </div>

        {/* Journey narrative */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className={`lg:col-span-7 ${reveal(backgroundRevealed)}`} style={{ '--d': '120ms' }}>
            <h3 className="text-statement mb-6">Non-linear path to building AI products</h3>
            <div className="space-y-4 text-body text-muted">
              <p>
                Started in sales—built and led teams of 10-12 people. That sparked
                an interest in tech, which led to a pivot into data science.
              </p>
              <p>
                Spent years in the trenches: marketing attribution models, churn prediction,
                likelihood-to-convert systems. Built custom analytics dashboards for
                enterprise clients. Learned what actually matters when you're shipping
                to real users with real stakes.
              </p>
              <p>
                Moved into fintech, then AI-powered content production, then enterprise
                intelligence. Each step closer to where AI creates the most leverage—and
                where trust matters most.
              </p>
            </div>
          </div>

          {/* Timeline visual - editorial horizontal */}
          <div className={`lg:col-span-5 ${reveal(backgroundRevealed)}`} style={{ '--d': '180ms' }}>
            <div className="relative">
              {/* The journey as connected blocks */}
              <div className="space-y-4">
                {[
                  { label: 'Sales', sub: 'Leadership' },
                  { label: 'Data', sub: 'Science' },
                  { label: 'Fintech', sub: 'YC S23' },
                  { label: 'AI', sub: 'Products' },
                  { label: 'Precurion', sub: 'Now', active: true }
                ].map((step, i) => (
                  <div
                    key={step.label}
                    className="flex items-center gap-4"
                  >
                    <div
                      className={`w-1 h-12 ${step.active ? 'bg-accent' : 'bg-border'}`}
                    />
                    <div>
                      <p className={`text-body font-medium ${step.active ? 'text-foreground' : 'text-muted'}`}>
                        {step.label}
                      </p>
                      <p className={`text-caption ${step.active ? 'text-accent' : 'text-subtle'}`}>
                        {step.sub}
                      </p>
                    </div>
                    {step.active && (
                      <div className="ml-auto">
                        <div className="w-2 h-2 rounded-full bg-accent animate-pulse" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
